import { makeCartApi } from "./api";
import { ModalError } from "./modal-error";
import { useAnalytics } from "../google-analytics";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useTranslation } from "next-i18next";
import NiceModal from "@ebay/nice-modal-react";
import React from "react";
export const CartContext = React.createContext({});
CartContext.displayName = "CartContext";
export const CartProvider = ({ api, children }) => {
    const { t } = useTranslation("c-cart");
    const errorMessages = React.useMemo(() => ({
        500: t("error-500", "Ein unbekannter Fehler ist aufgetreten. Bitte versuche es erneut oder kontaktiere uns unter support@culinario-mortale.de"),
    }), [t]);
    // we do this for dependency injection. we need access to i18n to make our api client
    const _api = React.useMemo(() => (api ? api : makeCartApi({ errorMessages })), [api, errorMessages]);
    const trackEvent = useAnalytics();
    const { data: cart, error: getCartError, isLoading: getCartIsLoading, refetch, } = useQuery(["cart"], _api.get);
    const { error: addItemError, isLoading: addItemIsLoading, mutate: handleAddItem, mutateAsync: handleAddItemAsync, } = useMutation(_api.addItem, {
        onSuccess: async (_, { id, quantity }) => {
            const { data } = await refetch();
            const item = data?.items.find((i) => i.id === id);
            if (!item)
                return;
            trackEvent("add_to_cart", {
                currency: item.prices.currency_code,
                value: parseInt(item.prices.price) / 100,
                items: [
                    {
                        index: 0,
                        item_id: id,
                        item_name: item.name,
                        quantity,
                    },
                ],
            });
        },
    });
    const { error: updateItemError, isLoading: updateItemIsLoading, mutate: handleUpdateItem, } = useMutation(_api.updateItem, { onSuccess: () => refetch() });
    const { error: removeError, isLoading: removeIsLoading, mutate: handleRemoveItem, } = useMutation(_api.removeItem, {
        onMutate: ({ key }) => {
            const item = cart?.items.find((i) => i.key === key);
            return { item };
        },
        onSuccess: async (_, __, ctx) => {
            refetch();
            if (!ctx || !ctx.item)
                return;
            const { item } = ctx;
            trackEvent("remove_from_cart", {
                currency: item.prices.currency_code,
                value: parseInt(item.prices.price) / 100,
                items: [
                    {
                        index: 0,
                        item_id: item.id,
                        item_name: item.name,
                        quantity: item.quantity,
                    },
                ],
            });
        },
    });
    const addItem = React.useCallback(({ id, quantity }) => handleAddItem({ id, quantity }), [handleAddItem]);
    const addItemAsync = React.useCallback(async ({ id, quantity }) => handleAddItemAsync({ id, quantity }), [handleAddItemAsync]);
    const updateItem = React.useCallback(({ key, quantity }) => handleUpdateItem({ key, quantity }), [handleUpdateItem]);
    const removeItem = React.useCallback(({ key }) => handleRemoveItem({ key }), [handleRemoveItem]);
    const error = addItemError || getCartError || removeError || updateItemError;
    const disableInteraction = addItemIsLoading || getCartIsLoading || removeIsLoading || updateItemIsLoading;
    React.useEffect(() => {
        if (error)
            NiceModal.show(ModalError, { error });
    }, [error]);
    return (React.createElement(CartContext.Provider, { value: {
            addItem,
            addItemAsync,
            cart,
            disableInteraction,
            error,
            errors: {
                addItem: addItemError,
                getCart: getCartError,
                removeItem: removeError,
                updateItem: updateItemError,
            },
            isLoading: {
                addItem: addItemIsLoading,
                getCart: getCartIsLoading,
                removeItem: removeIsLoading,
                updateItem: updateItemIsLoading,
            },
            removeItem,
            sessionToken: () => _api.getSession().token ?? "",
            updateItem,
        } }, children));
};
//# sourceMappingURL=provider.js.map